import { useState } from "react";
import { Edit, Check, X, Globe } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ClientTranslation } from "@/hooks/useClientTranslations";

interface TranslationTableProps {
  translations: ClientTranslation[];
  onEdit: (key: string, value: string, locale: string) => void;
  isLoading: boolean;
}

export function TranslationTable({
  translations,
  onEdit,
  isLoading,
}: TranslationTableProps) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleEdit = (translation: ClientTranslation) => {
    setEditingKey(translation.key);
    setEditValue(translation.clientText || "");
  };

  const handleSave = (translation: ClientTranslation) => {
    onEdit(translation.key, editValue, translation.locale);
    setEditingKey(null);
    setEditValue("");
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue("");
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Key</TableHead>
            <TableHead>Default Text</TableHead>
            <TableHead>Client Text</TableHead>
            <TableHead className="w-[150px]">Feature</TableHead>
            <TableHead className="w-[100px]">Status</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {translations.map((translation) => (
            <TableRow key={translation.key}>
              <TableCell className="font-mono text-sm">
                {translation.key}
              </TableCell>
              <TableCell className="max-w-[300px]">
                <div className="truncate" title={translation.defaultText}>
                  {translation.defaultText}
                </div>
              </TableCell>
              <TableCell className="max-w-[300px]">
                {editingKey === translation.key ? (
                  <Input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSave(translation);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        handleCancel();
                      }
                    }}
                    className="h-8"
                    autoFocus
                  />
                ) : (
                  <div className="truncate" title={translation.clientText || ""}>
                    {translation.clientText || (
                      <span className="text-muted-foreground italic">
                        Using default
                      </span>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell>
                {translation.feature && (
                  <Badge variant="outline" className="capitalize">
                    {translation.feature}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {translation.isModified ? (
                  <Badge variant="default" className="bg-green-600">
                    <Globe className="h-3 w-3 mr-1" />
                    Custom
                  </Badge>
                ) : (
                  <Badge variant="secondary">Default</Badge>
                )}
              </TableCell>
              <TableCell>
                {editingKey === translation.key ? (
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => handleSave(translation)}
                      title="Save changes"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={handleCancel}
                      title="Cancel editing"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => handleEdit(translation)}
                    title="Edit translation"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {translations.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                No translations found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}